import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChefHat, Truck, Leaf, Sparkles } from 'lucide-react';
import { Reveal, SplitText, AnimatedDivider, useParallax } from './effects';

const BUILDING_HERO =
  '/site/tarhun-bina-hero.webp';
const BUILDING_AERIAL =
  '/site/tarhun-bina-aerial.webp';

const KPIS = [
  { v: '2012', l: 'Kuruluş yılı' },
  { v: '120+', l: 'Kurumsal müşteri' },
  { v: '3.200', l: 'm² merkezi mutfak' },
  { v: '7/24', l: 'Operasyon hattı' },
];

const PILLARS = [
  {
    Icon: ChefHat,
    eyebrow: '01 · Mutfak',
    title: 'Şef imzalı, beslenme uzmanı onaylı menü.',
    body: 'Mevsiminde, dengelenmiş, sertifikalı. Her tabak — tutarlı bir sistemin çıktısıdır.',
  },
  {
    Icon: Truck,
    eyebrow: '02 · Lojistik',
    title: 'İzotermli filo, sıcak zincir disiplini.',
    body: 'GPS takipli izotermli araçlar; sıcaklık dakikası dakikasına, servis saatine kadar korunur.',
  },
  {
    Icon: Leaf,
    eyebrow: '03 · Şeffaflık',
    title: 'Kalori, makro, alerjen — net bildirim.',
    body: 'Diyetisyen onaylı içerik; üretimden tüketime izlenebilir, denetlenebilir bir akış.',
  },
];

const KurumsalSection = () => {
  const parallaxRef = useParallax(0.06);

  return (
    <section className="relative bg-[#F5F3EF] overflow-hidden pt-28 pb-32 lg:pt-40 lg:pb-44">
      {/* Giant background wordmark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-8 lg:-bottom-16 left-0 right-0 select-none"
      >
        <div
          className="font-display text-center text-[26vw] lg:text-[22vw] leading-[0.78] tracking-[-0.04em] font-medium whitespace-nowrap"
          style={{ color: '#72272D', opacity: 0.045 }}
        >
          TARHUN
        </div>
      </div>

      {/* Dot grid micro-texture (matches About) */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
          backgroundSize: '36px 36px',
        }}
      />

      <div className="container-x relative">
        {/* TOP GRID — text + collage */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT — Editorial copy */}
          <div className="lg:col-span-5">
            {/* Pill eyebrow */}
            <Reveal delay={80} y={10} duration={700}>
              <div
                className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
                style={{
                  background: 'rgba(201,168,117,0.10)',
                  border: '1px solid rgba(201,168,117,0.35)',
                }}
              >
                <Sparkles size={13} className="text-[#9A7F4F]" strokeWidth={1.6} />
                <span className="text-[10.5px] tracking-[0.3em] uppercase text-[#72272D] font-medium">
                  Kurumsal · Tarhun Hikayesi
                </span>
              </div>
            </Reveal>

            <h2
              className="mt-7 font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[1.02] tracking-[-0.022em] text-[#1A1A1A]"
              style={{ fontWeight: 500 }}
            >
              <SplitText text="Bir yemek firması değil," delay={120} />
              <br />
              <span className="italic font-light text-[#72272D]">
                <SplitText text="bir kurumsal sistem." delay={520} />
              </span>
            </h2>

            <Reveal delay={1000}>
              <div className="mt-8 space-y-5 text-[15.5px] lg:text-[17px] text-[#1A1A1A]/72 leading-[1.7] max-w-xl">
                <p>
                  2012'den bu yana sanayinin merkezi <strong className="font-medium text-[#1A1A1A]">Kocaeli'de</strong>; otomotiv, kimya, ilaç ve enerji
                  sektörlerinin günlük yemek operasyonunu yönetiyoruz. Tek hedefimiz — müşterimizin yemek konusunu zihninden tamamen çıkarmak.
                </p>
                <p>
                  HACCP sertifikalı merkezi mutfak, izotermli filo, beslenme uzmanı ve atanmış müşteri yöneticisi modelimizle Türkiye'nin önde gelen kuruluşlarına 365 gün — aynı saat, aynı kalite, aynı disiplinle hizmet veriyoruz.
                </p>
              </div>
            </Reveal>

            <Reveal delay={1150}>
              <div className="mt-10 flex items-center gap-7">
                <Link
                  to="/hakkimizda"
                  className="group inline-flex items-center gap-3 bg-[#72272D] text-[#F5F0E5] px-8 py-[18px] text-[11.5px] tracking-[0.24em] uppercase font-semibold rounded-sm transition-colors duration-300 hover:bg-[#5C1F25]"
                  data-testid="kurumsal-cta-primary"
                >
                  Hikayemizi İncele
                  <ArrowRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
                <Link
                  to="/iletisim"
                  className="group inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-medium text-[#1A1A1A] border-b border-[#9A7F4F]/45 pb-1.5 transition-colors duration-300 hover:text-[#72272D] hover:border-[#72272D]"
                >
                  Tesisi Ziyaret Et
                  <ArrowRight
                    size={13}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </Reveal>

            {/* KPIs */}
            <div className="mt-14 lg:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-10">
              {KPIS.map((k, i) => (
                <Reveal key={k.l} delay={1300 + i * 100} y={20}>
                  <div className="border-t border-[#9A7F4F]/35 pt-4">
                    <div className="font-display text-[#1A1A1A] numeric text-[32px] lg:text-[40px] leading-none tracking-[-0.02em] font-medium">
                      {k.v}
                    </div>
                    <div className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/60 font-medium">
                      {k.l}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* RIGHT — Collage: main building + overlapping aerial + floating chip */}
          <div className="lg:col-span-7 relative">
            <Reveal delay={250} y={40} duration={900}>
              <div className="relative">
                {/* Main building image — HORIZONTAL hero (cinematic landscape) */}
                <div className="relative aspect-[4/3] overflow-hidden rounded-sm shadow-[0_40px_100px_-30px_rgba(114,39,45,0.45)] w-full">
                  <img
                    ref={parallaxRef}
                    src={BUILDING_HERO}
                    alt="Tarhun Catering merkez tesisi — bina ve filo"
                    className="absolute inset-0 w-full h-full object-cover will-change-transform"
                    style={{ transform: 'scale(1.06)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C0B]/55 via-transparent to-transparent" />
                </div>

                {/* Floating SERTIFIKALI chip — top right */}
                <div className="absolute -top-6 right-0 lg:-right-4 z-20 hidden sm:block">
                  <div
                    className="px-6 py-4 bg-[#0A0C0B] text-[#F5F0E5] rounded-sm shadow-[0_25px_60px_-20px_rgba(10,12,11,0.55)]"
                    style={{ minWidth: '210px' }}
                  >
                    <div className="text-[10px] tracking-[0.34em] uppercase text-[#C9A875] font-medium">
                      Sertifikalı
                    </div>
                    <div className="font-display text-[18px] lg:text-[20px] mt-1.5 leading-tight tracking-[-0.005em]">
                      HACCP · ISO&nbsp;22000
                    </div>
                  </div>
                </div>

                {/* Overlapping aerial image — bottom left */}
                <div className="absolute -bottom-14 left-0 lg:-left-10 w-[44%] lg:w-[36%] aspect-[4/3] overflow-hidden rounded-sm shadow-[0_30px_70px_-20px_rgba(0,0,0,0.55)] ring-[6px] ring-[#F5F3EF]">
                  <img
                    src={BUILDING_AERIAL}
                    alt="Tarhun Catering — havadan görünüm"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C0B]/30 to-transparent" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* BOTTOM — 3 pillar cards */}
        <div className="mt-32 lg:mt-44 relative">
          <div className="flex items-center gap-3 mb-10">
            <AnimatedDivider width="3rem" />
            <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium">
              Üç Direkli Sistem
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.eyebrow} delay={i * 140} y={28}>
                <div
                  className="group relative h-full p-8 lg:p-10 bg-[#FBF9F4] rounded-sm border border-[#9A7F4F]/15 transition-all duration-500 hover:border-[#72272D]/40 hover:shadow-[0_30px_60px_-30px_rgba(114,39,45,0.30)]"
                >
                  <div
                    className="inline-flex items-center justify-center h-12 w-12 rounded-sm mb-7 transition-colors duration-500"
                    style={{
                      background: 'rgba(114,39,45,0.06)',
                      color: '#72272D',
                    }}
                  >
                    <p.Icon size={22} strokeWidth={1.4} />
                  </div>

                  <div className="text-[10px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium mb-3">
                    {p.eyebrow}
                  </div>

                  <h3
                    className="font-display text-[22px] lg:text-[26px] text-[#1A1A1A] leading-[1.18] tracking-[-0.01em]"
                    style={{ fontWeight: 500 }}
                  >
                    {p.title}
                  </h3>

                  <p className="mt-5 text-[14.5px] text-[#1A1A1A]/68 leading-[1.7]">
                    {p.body}
                  </p>

                  {/* Bottom hairline that animates on hover */}
                  <div className="absolute bottom-0 left-0 h-px bg-[#72272D] w-0 group-hover:w-full transition-all duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default KurumsalSection;
